import { CssUtils } from '@common/flare-data/util/css-utils';

/** The whitelist of CSS properties allowed in preview styles. */
const previewStyleProperties = new Set<string>([
  'color',
  'font',
  'font-family',
  'font-size',
  'font-style',
  'font-variant',
  'font-weight',
  'letter-spacing',
  'text-align',
  'text-decoration',
  'text-indent',
  'text-transform',
  'word-spacing',
  'background',
  'background-color',
  'background-image',
  'background-position',
  'background-repeat',
  'background-size',
  'background-clip',
  'background-origin',
  'border',
  'border-width',
  'border-style',
  'border-color',
  'border-radius',
  'border-left',
  'border-left-width',
  'border-left-style',
  'border-left-color',
  'border-left-radius',
  'border-top',
  'border-top-width',
  'border-top-style',
  'border-top-color',
  'border-top-radius',
  'border-right',
  'border-right-width',
  'border-right-style',
  'border-right-color',
  'border-right-radius',
  'border-bottom',
  'border-bottom-width',
  'border-bottom-style',
  'border-bottom-color',
  'border-bottom-radius',
  'text-shadow',
  'opacity'
]);

/** Checks whether the CSS property is allowed in LWE preview styles. */
export function isLwePreviewStyleProperty(propertyName: string): boolean {
  return previewStyleProperties.has(propertyName) || propertyName?.startsWith('--');
}

/** Converts CSS property name for the LWE editor stylesheet. */
export function toLweEditorStylePropertyName(propertyName: string): string {
  return propertyName.replace(/^mc-/gi, '--mc-');
}

/** Converts the specified selector to a selector for LWE preview styles. */
export function patchSelectorForLwePreviewStylesheet(selectorList: string, selectorPrefix: string = null): string {
  const tagPrefix = 'mc-preview-tag-';
  const nodeIdPrefix = 'mc-preview-id-';

  const renames = {
    'PseudoClassSelector': {
      'ClassSelector': {
        '^root$': `${tagPrefix}html`
      }
    },
    'TypeSelector': {
      'ClassSelector': {
        '^MadCap\\|(.+)$': `${tagPrefix}madcap-$1`,
        '^[a-zA-Z][a-zA-Z0-9-]*$': `${tagPrefix}$&`
      }
    },
    'IdSelector': {
      'ClassSelector': {
        '^': nodeIdPrefix
      }
    },
  };

  return CssUtils.renameSelectors(selectorList, renames, selectorPrefix);
}

/** Converts the specified selector to a selector for the LWE text editor. */
export function patchSelectorForLweEditorStylesheet(selectorList: string, selectorPrefix: string = null): string {
  const tagPrefix = 'mc-lwe-';

  const renames = {
    'PseudoClassSelector': {
      'TypeSelector': {
        '^root$': `${tagPrefix}html`
      }
    },
    'TypeSelector': {
      'TypeSelector': {
        '^MadCap\\|qrCode$': `${tagPrefix}qrcode`,
        '^MadCap\\|slideshow$': `${tagPrefix}slideshow-viewport`,
        '^MadCap\\|slideshowBullet$': `${tagPrefix}slideshowbullet`,
        '^MadCap\\|(.+)$': `MadCap\\:$1`,
        '^(html|body|frameset|frame|noframes|base|title|applet|canvas|datalist|rp|wbr|noscript|iframe|script|template)$': `${tagPrefix}$1`,
      }
    },
  };

  return CssUtils.renameSelectors(selectorList, renames, selectorPrefix);
}
